import type { BenefitCategory } from "@yutai/shared"
import { MARKET_CAP_TIER_LABELS, type MarketCapTier } from "../../lib/marketCapTier.js"
import { RELEVANCE_LEVEL_LABELS, type RelevanceLevel } from "../../lib/relevance.js"
import type { Filters } from "./useBenefits.js"

export const FILTER_CATEGORIES: { value: BenefitCategory; label: string }[] = [
  { value: "food", label: "食品・飲料" },
  { value: "dining", label: "外食" },
  { value: "travel", label: "旅行・宿泊" },
  { value: "discount", label: "割引・優待券" },
  { value: "quo", label: "QUO・商品券" },
  { value: "catalog", label: "カタログ" },
  { value: "digital", label: "デジタルギフト" },
  { value: "own", label: "自社製品・サービス" },
  { value: "donation", label: "寄付" },
  { value: "tour", label: "施設見学" },
  { value: "other", label: "その他" },
]

export const FILTER_MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)

export const HOLDING_YEAR_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "1年" },
  { value: 2, label: "2年" },
  { value: 3, label: "3年" },
  { value: 4, label: "4年" },
  { value: 5, label: "5年" },
  { value: 6, label: "それ以上" },
]

export const MIN_BENEFIT_VALUE_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: "制限なし" },
  { value: 1000, label: "1,000円以上" },
  { value: 3000, label: "3,000円以上" },
  { value: 5000, label: "5,000円以上" },
  { value: 10000, label: "10,000円以上" },
]

export const MIN_YIELD_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: "制限なし" },
  { value: 1, label: "1%以上" },
  { value: 2, label: "2%以上" },
  { value: 3, label: "3%以上" },
  { value: 4, label: "4%以上" },
  { value: 5, label: "5%以上" },
]

export const RELEVANCE_LEVEL_OPTIONS: { value: RelevanceLevel; label: string }[] = [
  { value: "high", label: RELEVANCE_LEVEL_LABELS.high },
  { value: "mid", label: RELEVANCE_LEVEL_LABELS.mid },
  { value: "low", label: RELEVANCE_LEVEL_LABELS.low },
]

export const CAP_TIER_OPTIONS: { value: MarketCapTier; label: string }[] = [
  { value: "small", label: MARKET_CAP_TIER_LABELS.small },
  { value: "mid", label: MARKET_CAP_TIER_LABELS.mid },
  { value: "large", label: MARKET_CAP_TIER_LABELS.large },
]

export interface ActiveFilterTag {
  id: string
  label: string
  remove: Partial<Filters>
}

export function getActiveFilterTags(filters: Filters): ActiveFilterTag[] {
  const tags: ActiveFilterTag[] = []

  for (const cat of filters.categories) {
    const label = FILTER_CATEGORIES.find((c) => c.value === cat)?.label ?? cat
    tags.push({
      id: `cat-${cat}`,
      label,
      remove: { categories: filters.categories.filter((c) => c !== cat) },
    })
  }

  for (const m of filters.months) {
    tags.push({
      id: `month-${m}`,
      label: `権利確定 ${m}月`,
      remove: { months: filters.months.filter((x) => x !== m) },
    })
  }

  for (const m of filters.deliveryMonths) {
    tags.push({
      id: `delivery-${m}`,
      label: `送付 ${m}月`,
      remove: { deliveryMonths: filters.deliveryMonths.filter((x) => x !== m) },
    })
  }

  for (const industry of filters.industries) {
    tags.push({
      id: `industry-${industry}`,
      label: industry,
      remove: { industries: filters.industries.filter((i) => i !== industry) },
    })
  }

  if (filters.minBenefitValue !== null) {
    const label =
      MIN_BENEFIT_VALUE_OPTIONS.find((o) => o.value === filters.minBenefitValue)?.label ??
      `${filters.minBenefitValue.toLocaleString()}円以上`
    tags.push({
      id: `minValue-${filters.minBenefitValue}`,
      label,
      remove: { minBenefitValue: null },
    })
  }

  if (filters.minDividendYield !== null) {
    const label =
      MIN_YIELD_OPTIONS.find((o) => o.value === filters.minDividendYield)?.label ??
      `${filters.minDividendYield}%以上`
    tags.push({
      id: `minDividendYield-${filters.minDividendYield}`,
      label: `配当利回り ${label}`,
      remove: { minDividendYield: null },
    })
  }

  if (filters.minBenefitYield !== null) {
    const label =
      MIN_YIELD_OPTIONS.find((o) => o.value === filters.minBenefitYield)?.label ??
      `${filters.minBenefitYield}%以上`
    tags.push({
      id: `minBenefitYield-${filters.minBenefitYield}`,
      label: `優待利回り ${label}`,
      remove: { minBenefitYield: null },
    })
  }

  if (filters.minCombinedYield !== null) {
    const label =
      MIN_YIELD_OPTIONS.find((o) => o.value === filters.minCombinedYield)?.label ??
      `${filters.minCombinedYield}%以上`
    tags.push({
      id: `minYield-${filters.minCombinedYield}`,
      label: `総合利回り ${label}`,
      remove: { minCombinedYield: null },
    })
  }

  if (filters.longTermBonus != null) {
    const label =
      HOLDING_YEAR_OPTIONS.find((o) => o.value === filters.longTermBonus)?.label ??
      `${filters.longTermBonus}年`
    tags.push({
      id: "longTermBonus",
      label: `長期保有 ${label}`,
      remove: { longTermBonus: null },
    })
  }

  for (const level of filters.relevance) {
    tags.push({
      id: `relevance-${level}`,
      label: `事業との関連 ${RELEVANCE_LEVEL_LABELS[level]}`,
      remove: { relevance: filters.relevance.filter((r) => r !== level) },
    })
  }

  for (const tier of filters.capTiers) {
    tags.push({
      id: `capTier-${tier}`,
      label: MARKET_CAP_TIER_LABELS[tier],
      remove: { capTiers: filters.capTiers.filter((t) => t !== tier) },
    })
  }

  return tags
}

export function countActiveFilters(filters: Filters): number {
  return getActiveFilterTags(filters).length
}

/** @deprecated use countActiveFilters */
export function countDetailFilters(filters: Filters): number {
  return countActiveFilters(filters)
}
